import React, { useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react-dom";
import { LoaderCircle, Send } from "lucide-react";
import usePopoverDismiss from "../../hooks/usePopoverDismiss.js";
import { handleBillingDialogKeyDown } from "../../utils/billingDialogAccessibility.js";
import { INCOMPLETE_EMAIL_MESSAGE, isCompleteEmail } from "../../utils/billingPresentation.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";

const BUTTON = "inline-flex h-7 items-center justify-center gap-1 rounded-[4px] border px-2 text-xs font-bold focus-visible:outline-2 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";

/** Le destinataire est confirmé avant de numéroter le relevé et d'envoyer son PDF.
 * defaultOpen : panneau ouvert dès l'affichage (« Envoyer » du clic droit d'une carte).
 * version : celle du relevé affiché ; si elle change pendant la confirmation, l'envoi attend une nouvelle vérification. */
export default function TransportStatementSendButton({ recipient = "", canEditRecipient = true, className, defaultOpen = false, disabled = false, pending = false, title, warning = "", version, onSend, onDirtyChange }) {
  const [open, setOpen] = useState(defaultOpen);
  const [shownVersion, setShownVersion] = useState(version);
  const changed = open && version !== shownVersion;
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(recipient);
  const incomplete = Boolean(email.trim()) && !isCompleteEmail(email);
  const sending = useRef(false);
  const trigger = useRef(null);
  const input = useRef(null);
  const id = useId();
  React.useLayoutEffect(() => {
    onDirtyChange?.(open && email !== recipient);
    return () => onDirtyChange?.(false);
  }, [open, email, recipient, onDirtyChange]);
  const { refs, floatingStyles, isPositioned } = useFloating({ open, placement: "top-end", strategy: "fixed",
    middleware: [offset(6), flip({ padding: 12 }), shift({ padding: 12 }), size({ padding: 12,
      apply({ availableWidth, availableHeight, elements }) {
        Object.assign(elements.floating.style, { maxWidth: `${Math.max(0, availableWidth)}px`, maxHeight: `${Math.max(0, availableHeight)}px` });
      },
    })], whileElementsMounted: autoUpdate });
  const close = () => {
    if (sending.current) return;
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  };
  usePopoverDismiss({ anchorElement: trigger.current, floatingRef: refs.floating, isOpen: open && !busy, onClose: close });
  React.useEffect(() => {
    if (open && isPositioned) input.current?.focus({ preventScroll: true });
  }, [open, isPositioned]);
  const send = async (event) => {
    event.preventDefault();
    if (sending.current || disabled || pending || changed || !email.trim() || incomplete) return;
    sending.current = true;
    setBusy(true);
    try {
      const result = await onSend(email.trim());
      if (result !== null) setOpen(false);
    } finally {
      sending.current = false;
      setBusy(false);
      trigger.current?.focus({ preventScroll: true });
    }
  };
  return <>
    <button ref={(node) => { trigger.current = node; refs.setReference(node); }} type="button" className={className}
      disabled={disabled || pending || busy || (!canEditRecipient && !recipient.trim())}
      title={title || (!canEditRecipient && !recipient.trim() ? "Courriel de facturation absent : demandez à une personne qui a « Gérer les brouillons »." : undefined)}
      aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => { if (open) close(); else { setEmail(recipient); setShownVersion(version); setOpen(true); } }}>
      {pending ? <><LoaderCircle className="animate-spin" size={14} />Vérification…</> : <><Send size={14} />Envoyer le relevé</>}
    </button>
    {open ? createPortal(<form ref={refs.setFloating} id={id} role="dialog" aria-modal="false" aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`} aria-busy={busy} onSubmit={send}
      onKeyDown={(event) => {
        event.stopPropagation();
        handleBillingDialogKeyDown(event, { activeElement: document.activeElement, dialog: refs.floating.current, onClose: close });
      }}
      style={{ ...floatingStyles, width: "min(24rem, calc(100vw - 1.5rem))", visibility: isPositioned ? "visible" : "hidden" }}
      className="z-popover grid gap-2 overflow-y-auto rounded-[4px] border border-slate-300 bg-white p-3 text-xs text-slate-700 shadow-xl">
      <h2 id={`${id}-title`} className="text-sm font-bold text-slate-900">Envoyer le relevé ?</h2>
      <p id={`${id}-description`}>Confirmez l’adresse courriel qui recevra le relevé et son PDF.</p>
      <label className="grid gap-1 font-semibold" htmlFor={`${id}-email`}>Courriel destinataire
        <input ref={input} id={`${id}-email`} type="email" required maxLength={255} value={email} readOnly={!canEditRecipient} disabled={busy}
          onChange={(event) => setEmail(event.target.value)}
          className="h-6 min-w-0 rounded-[4px] border border-slate-300 bg-white px-2 py-0 font-normal text-slate-900 focus:border-accent focus:outline-2 focus:outline-accent" />
        {incomplete ? <span className="font-semibold text-[#9f3a33]">{INCOMPLETE_EMAIL_MESSAGE}</span> : null}
      </label>
      <p>{canEditRecipient ? "Cette adresse s’applique uniquement à ce relevé. La fiche de l’entreprise reste inchangée." : "Le droit « Gérer les brouillons » est requis pour modifier cette adresse."}</p>
      <p>Le relevé reçoit son numéro RT-{new Date().getFullYear()}-… puis part par courriel. Le brouillon ne pourra plus être modifié.</p>
      {warning ? <p className="rounded-[4px] border border-amber-600 bg-white px-2 py-1 text-amber-900">{warning} Elles iront dans un prochain relevé.</p> : null}
      {changed ? <p role="alert" className="rounded-[4px] border border-[#c5524a] bg-white px-2 py-1 font-semibold text-[#9f3a33]">Ce relevé vient d’être mis à jour. Faites « Retour », vérifiez le montant, puis relancez l’envoi.</p> : null}
      <div className="flex justify-end gap-2">
        <button type="button" className={`${BUTTON} border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50`} disabled={busy} onClick={close}>Retour</button>
        <button type="submit" className={`${BUTTON} border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong`}
          disabled={busy || disabled || pending || changed || !email.trim() || incomplete}>
          {busy ? <LoaderCircle size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
          {busy ? "Envoi…" : warning ? "Envoyer quand même" : "Envoyer le relevé"}
        </button>
      </div>
    </form>, getReactPortalRoot()) : null}
  </>;
}
